import { ArrowUpRightIcon, XIcon } from 'lucide-react';
import { createContext, useContext, type ComponentPropsWithRef, type ReactElement } from 'react';
import { cn } from '../util/classes.ts';
import { PolymorphicButtonBase, type PolymorphicButtonBaseProps } from './button.tsx';

type ValueChipVariant = 'value' | 'suggestion';

const ValueChipLabelContext = createContext('');

export type ValueChipProps = ComponentPropsWithRef<'span'> & {
    variant?: ValueChipVariant;
    /** The chip's text, and the accessible name of its body. */
    label: string;
};

/** The field-value frame: `suggestion` is dashed and empty, `value` holds a set value. */
export function ValueChip({ variant = 'value', label, className, ...props }: ValueChipProps): ReactElement {
    return (
        <ValueChipLabelContext.Provider value={label}>
            <span
                data-slot="value-chip"
                data-variant={variant}
                className={cn(
                    'group inline-flex h-interactive w-fit min-w-0 items-center rounded-default border border-line-weak',
                    'text-ui-base transition duration-state ease-state',
                    'hover:border-line-strong has-[[data-slot=value-chip-body]:active]:translate-px',
                    'data-[variant=value]:bg-container data-[variant=value]:text-content',
                    'data-[variant=suggestion]:border-dashed data-[variant=suggestion]:text-content-weaker',
                    'data-[variant=suggestion]:hover:text-content',
                    'has-data-[slot=value-chip-remove]:gap-tight has-data-[slot=value-chip-remove]:pr-tight',
                    className,
                )}
                {...props}
            />
        </ValueChipLabelContext.Provider>
    );
}

export type ValueChipBodyProps = PolymorphicButtonBaseProps;

/** The clickable content area, named by `label`. `href` / `to` make it navigate instead of act,
    and bring the trailing ↗ with them. */
export function ValueChipBody({ className, children, ...props }: ValueChipBodyProps): ReactElement {
    const label = useContext(ValueChipLabelContext);
    const navigates = props.href !== undefined || props.to !== undefined;

    return (
        <PolymorphicButtonBase
            data-slot="value-chip-body"
            aria-label={label}
            className={cn(
                'flex h-full min-w-0 cursor-pointer items-center gap-cozy px-default text-inherit select-none',
                'group-has-data-[slot=value-chip-remove]:pr-0',
                "[&_svg:not([class*='size-'])]:size-4",
                '[&_svg]:shrink-0 [&_svg]:text-content-weaker',
                className,
            )}
            {...props}
        >
            {children}
            {navigates && (
                <ArrowUpRightIcon className="size-3.5 transition-colors group-hover:text-content" />
            )}
        </PolymorphicButtonBase>
    );
}

export type ValueChipLabelProps = ComponentPropsWithRef<'span'>;

/** The chip's `label`, truncated. Children override it. */
export function ValueChipLabel({ className, children, ...props }: ValueChipLabelProps): ReactElement {
    const label = useContext(ValueChipLabelContext);

    return (
        <span data-slot="value-chip-label" className={cn('truncate', className)} {...props}>
            {children ?? label}
        </span>
    );
}

export type ValueChipRemoveProps = Omit<ComponentPropsWithRef<'button'>, 'aria-label'> & {
    /** Names the action for assistive technology, e.g. `„Moritz Karl“ entfernen`. */
    label: string;
};

/** Clears the value. Its presence is what makes the chip removable. */
export function ValueChipRemove({
    className,
    label,
    children,
    ...props
}: ValueChipRemoveProps): ReactElement {
    return (
        <button
            type="button"
            data-slot="value-chip-remove"
            aria-label={label}
            className={cn(
                'flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-default text-content-weaker',
                'transition select-none hover:bg-content/(--opacity-wash) hover:text-content active:translate-px',
                className,
            )}
            {...props}
        >
            {children ?? <XIcon className="size-3.5" />}
        </button>
    );
}
